import { useCallback, useEffect, useState } from 'react';
import {
  clearDeferredPrompt,
  getAndroidModalOpen,
  getDeferredPrompt,
  getIosGuideOpen,
  initPwaInstallCapture,
  isIosDevice,
  isIosSafari,
  isStandaloneDisplay,
  markInstallDismissed,
  markInstallModalShown,
  promptPwaInstall,
  setAndroidModalOpen,
  setIosGuideOpen,
  subscribePwaInstall,
  wasInstallDismissedRecently,
  wasInstallModalShown,
} from './pwaInstallStore';

/**
 * Estado compartilhado de instalação PWA (Android prompt + guia iOS).
 */
export function usePwaInstall() {
  const [, setTick] = useState(0);

  useEffect(() => {
    initPwaInstallCapture();
    return subscribePwaInstall(() => setTick((n) => n + 1));
  }, []);

  const standalone = isStandaloneDisplay();
  const ios = isIosDevice();
  const iosSafari = isIosSafari();
  const deferredPrompt = getDeferredPrompt();
  const canPromptInstall = Boolean(deferredPrompt) && !standalone;
  const dismissed = wasInstallDismissedRecently();

  const showBanner =
    !standalone &&
    !dismissed &&
    (canPromptInstall || iosSafari);

  const promptInstall = useCallback(async () => promptPwaInstall(), []);

  const dismissInstall = useCallback(() => {
    markInstallDismissed();
    clearDeferredPrompt();
    setAndroidModalOpen(false);
    setIosGuideOpen(false);
  }, []);

  const openIosGuide = useCallback(() => setIosGuideOpen(true), []);
  const closeIosGuide = useCallback(() => setIosGuideOpen(false), []);

  const openAndroidModal = useCallback(() => {
    if (canPromptInstall) setAndroidModalOpen(true);
  }, [canPromptInstall]);

  const closeAndroidModal = useCallback(() => setAndroidModalOpen(false), []);

  /** Modal pós-login Android: só se o banner não estiver visível (evita empilhar chrome). */
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const isMobileViewport = window.matchMedia('(max-width: 768px)').matches;
    if (!isMobileViewport) return;
    if (standalone || dismissed || !canPromptInstall) return;
    if (showBanner) return;
    if (wasInstallModalShown()) return;
    markInstallModalShown();
    setAndroidModalOpen(true);
  }, [standalone, dismissed, canPromptInstall, showBanner]);

  return {
    isStandalone: standalone,
    isIos: ios,
    isIosSafari: iosSafari,
    canPromptInstall,
    showBanner,
    promptInstall,
    dismissInstall,
    iosGuideOpen: getIosGuideOpen(),
    openIosGuide,
    closeIosGuide,
    androidModalOpen: getAndroidModalOpen(),
    openAndroidModal,
    closeAndroidModal,
  };
}

export default usePwaInstall;
